import { defer, from, Observable, of } from "rxjs"
import { buffer, catchError, concatMap, debounceTime, delay, map, switchMap, tap } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { flatMap } from "lodash"
import moment from "moment-timezone"
import type { EpicDependencies } from "../rxjs/configureEpics"
import {
    ACCESS_LEVEL_CREATED,
    ACCESS_LEVEL_DELETED,
    ACCESS_LEVEL_UPDATED,
    ACCOUNT_IMAGE_DELETED,
    INVENTORY_ITEM_CREATED,
    INVENTORY_ITEM_DELETED,
    INVENTORY_ITEM_LOCATION_CREATED,
    INVENTORY_ITEM_LOCATION_DELETED,
    INVENTORY_ITEM_TRANSACTION_CREATED,
    INVENTORY_ITEM_UPDATED,
    PERMISSION_CREATED,
    PERMISSION_DELETED,
    SETTING_CREATED,
    SETTING_UPDATED,
    TIME_RECORD_CREATED,
    TIME_RECORD_DELETED,
    TIME_RECORD_UPDATED,
} from "../data/actions"
import {
    SEND_OPERATIONS_REQUESTED,
    SEND_OPERATIONS_SUCCESS,
    sendOperationsClearState,
    sendOperationsFailure,
    sendOperationsRequested,
    sendOperationsSuccess,
} from "./actions"
import { addAuditToOperation, createArrayOfOperationsToSend, createOperation } from "./utils"
import type {
    AccessData,
    InventoryItem,
    InventoryItemLocation,
    InventoryItemTransaction,
    PermissionData,
    Setting,
    TimesheetRecord,
} from "../data/types"
import { OperationType, RecordTypeEnum } from "../../api/types"
import {
    getAuthUserId,
    getHttpHeaders,
    getLocationDataByTerminalId,
    getSelectedAccount,
    getSelectedAccountId,
} from "../auth/selectors"
import type { SendOperationsResponse } from "./types"
import { getVersionById } from "./selectors"
import { createEventRequest, requestEvent } from "../../events/utils"
import { EventType } from "../../events/types"
import { getUtcTimestamp } from "../../utils/dateFunctions"
import { NotificationType, openNotification } from "../uiKit/Notification/Notification"
import * as uuid from "uuid"
import { timesheetRequestFailed, timesheetRequestSuccess } from "../screens/reports/timesheet/actions"
import { Action, StateObject } from "../redux/types"
import { AnalyticsFeature, AnalyticsFeatureMetadata } from "@figurepos/fgr-lib-business-logic/lib/analytics/types"
import { sendFeatureTracked } from "../screens/organization/FeatureTracking/hooks"
import { logger } from "../../utils/loggerWrap"

// TIMESHEET //
const trackTimesheetChange = (
    deps: EpicDependencies,
    action: AnalyticsFeatureMetadata[AnalyticsFeature.TimesheetChanges]["action"]
) => {
    sendFeatureTracked(getHttpHeaders(deps.getState()), getSelectedAccountId(deps.getState()) as string, {
        featureName: AnalyticsFeature.TimesheetChanges,
        metadata: { action: action },
    }).catch(e => {
        logger.error("trackTimesheetChange - Could not track feature", null, e)
    })
}

export const createTimeRecordEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(TIME_RECORD_CREATED),
        switchMap((action: Action) => {
            const tr: TimesheetRecord = action.payload.timeRecord
            return defer(() => {
                const accountId = getSelectedAccountId(deps.getState())
                const location = getLocationDataByTerminalId(deps.getState(), action.payload.terminalId)
                return requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.TimeRecordCreateRequested,
                        {
                            accountId: accountId as string,
                            locationId: location?.id as string,
                            correlationId: deps.idgen.next(),
                        },
                        {
                            id: uuid.v4(),
                            accountId: accountId as string,
                            editorUserAccountId: getSelectedAccount(deps.getState())?.userAccountId,
                            userAccountId: tr.userAccountId,
                            roleId: tr.roleId,
                            hourlyRate: (tr.hourlyRate ?? 0).toString(),
                            rateType: null,
                            clockInTimestamp: tr.clockInTimestamp,
                            clockInUserAccountId: tr.clockInUserAccountId,
                            clockInLocationId: tr.clockInLocationId,
                            clockInTerminalId: null,
                            clockOutTimestamp: tr.clockOutTimestamp,
                            clockOutUserAccountId: tr.clockOutUserAccountId,
                            clockOutLocationId: tr.clockOutLocationId,
                            clockOutTerminalId: null,
                            breaks: tr.breaks ?? undefined,
                        }
                    )
                )
            }).pipe(
                tap(() => trackTimesheetChange(deps, "shiftCreated")),
                switchMap(() => [timesheetRequestSuccess()]),
                catchError(() => {
                    openNotification(
                        NotificationType.Error,
                        "Could not create shift",
                        "Please try again later or contact support."
                    )
                    return [timesheetRequestFailed()]
                })
            )
        })
    )

export const updateTimeRecordEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(TIME_RECORD_UPDATED),
        concatMap((action: Action) => {
            const tr: Partial<TimesheetRecord> & { id: string } = action.payload.newValue
            return defer(() => {
                const accountId = getSelectedAccountId(deps.getState())
                const location = getLocationDataByTerminalId(deps.getState(), action.payload.terminalId)
                return requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.TimeRecordUpdateRequested,
                        {
                            accountId: accountId as string,
                            locationId: location?.id as string,
                            correlationId: deps.idgen.next(),
                        },
                        {
                            id: tr.id,
                            editorUserAccountId: getSelectedAccount(deps.getState())?.userAccountId,
                            userAccountId: tr.userAccountId,
                            roleId: tr.roleId,
                            hourlyRate: tr.hourlyRate?.toString(),
                            clockInTimestamp: tr.clockInTimestamp,
                            clockInUserAccountId: tr.clockInUserAccountId,
                            clockInLocationId: tr.clockInLocationId,
                            clockOutTimestamp: tr.clockOutTimestamp,
                            clockOutUserAccountId: tr.clockOutUserAccountId,
                            clockOutLocationId: tr.clockOutLocationId,
                            breaks: tr.breaks ?? [],
                        }
                    )
                )
            }).pipe(
                tap(() => trackTimesheetChange(deps, "shiftUpdated")),
                switchMap(() => [timesheetRequestSuccess()]),
                catchError(() => {
                    openNotification(
                        NotificationType.Error,
                        "Could not update shift",
                        "Please try again later or contact support."
                    )
                    return [timesheetRequestFailed()]
                })
            )
        })
    )

export const deleteTimeRecordEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(TIME_RECORD_DELETED),
        switchMap((action: Action) => {
            return defer(() => {
                const accountId = getSelectedAccountId(deps.getState())
                const location = getLocationDataByTerminalId(deps.getState(), action.payload.terminalId)
                return requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.TimeRecordDeleteRequested,
                        {
                            accountId: accountId as string,
                            locationId: location?.id as string,
                            correlationId: deps.idgen.next(),
                        },
                        {
                            id: action.payload.id,
                            editorUserAccountId: getSelectedAccount(deps.getState())?.userAccountId,
                        }
                    )
                )
            }).pipe(
                tap(() => trackTimesheetChange(deps, "shiftDeleted")),
                switchMap(() => [timesheetRequestSuccess()]),
                catchError(() => {
                    openNotification(
                        NotificationType.Error,
                        "Could not delete shift",
                        "Please try again later or contact support."
                    )
                    return [timesheetRequestFailed()]
                })
            )
        })
    )

// Access Levels/Roles

export const accessLevelCreatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCESS_LEVEL_CREATED),
        switchMap((action: Action) => {
            if (action.payload.useEventFlow) {
                return []
            }
            const access: AccessData = action.payload.accessLevel
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Insert,
                    RecordTypeEnum.AccessLevel,
                    access.id,
                    getVersionById(deps.getState(), access.id),
                    {
                        name: access.name,
                        origin_timestamp: moment().utc().format(),
                    }
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

export const accessLevelUpdatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCESS_LEVEL_UPDATED),
        switchMap((action: Action) => {
            if (action.payload.useEventFlow) {
                return []
            }
            const access: AccessData = action.payload.newValue
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Update,
                    RecordTypeEnum.AccessLevel,
                    access.id,
                    getVersionById(deps.getState(), access.id),
                    {
                        name: access.name,
                    }
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

export const accessLevelDeletedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCESS_LEVEL_DELETED),
        switchMap((action: Action) => {
            if (action.payload.useEventFlow) {
                return []
            }
            const id: string = action.payload.accessId
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Delete,
                    RecordTypeEnum.AccessLevel,
                    id,
                    getVersionById(deps.getState(), id),
                    {}
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )
// Permissions

export const permissionCreatedEpic = (actions$: Observable<any>, state$: Observable<any>, deps: EpicDependencies) =>
    actions$.pipe(
        ofType(PERMISSION_CREATED),
        switchMap((action: Action) => {
            if (action.payload.useEventFlow) {
                return []
            }
            const permission: PermissionData = action.payload.permission
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Insert,
                    RecordTypeEnum.Permission,
                    permission.id,
                    getVersionById(deps.getState(), permission.id),
                    {
                        name: permission.name,
                        access_level_id: permission.accessId,
                        origin_timestamp: moment().utc().format(),
                    }
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

export const permissionDeletedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(PERMISSION_DELETED),
        switchMap((action: Action) => {
            if (action.payload.useEventFlow) {
                return []
            }
            const permissionId = action.payload.permissionId
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Delete,
                    RecordTypeEnum.Permission,
                    permissionId,
                    getVersionById(deps.getState(), permissionId),
                    {}
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )
// Settings

export const settingCreatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SETTING_CREATED),
        switchMap((action: Action) => {
            const setting: Setting = action.payload.setting
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Insert,
                    RecordTypeEnum.Setting,
                    setting.id,
                    getVersionById(deps.getState(), setting.id),
                    {
                        key: setting.key,
                        value: setting.value,
                        location_id: setting.location_id,
                        terminal_id: setting.terminal_id || null,
                    }
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

export const settingUpdatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SETTING_UPDATED),
        switchMap((action: Action) => {
            const setting: Setting = action.payload.newValue
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Update,
                    RecordTypeEnum.Setting,
                    setting.id,
                    getVersionById(deps.getState(), setting.id),
                    {
                        key: setting.key,
                        value: setting.value,
                        location_id: setting.location_id,
                        terminal_id: setting.terminal_id || null,
                    }
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

// Inventory Item

export const inventoryItemCreatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_ITEM_CREATED),
        switchMap((action: Action) => {
            const inventoryItem: InventoryItem = action.payload.inventoryItem
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Insert,
                    RecordTypeEnum.InventoryItem,
                    inventoryItem.id,
                    getVersionById(deps.getState(), inventoryItem.id),
                    {
                        id: inventoryItem.id,
                        category_id: inventoryItem.categoryId,
                        is_restockable_on_void: inventoryItem.isRestockableOnVoid,
                        is_restockable_on_refund: inventoryItem.isRestockableOnRefund,
                        low_threshold: inventoryItem.lowThreshold,
                        origin_timestamp: getUtcTimestamp(),
                    }
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

export const inventoryItemUpdatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_ITEM_UPDATED),
        switchMap((action: Action) => {
            const inventoryItem: InventoryItem = action.payload.newValue
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Update,
                    RecordTypeEnum.InventoryItem,
                    inventoryItem.id,
                    getVersionById(deps.getState(), inventoryItem.id),
                    {
                        id: inventoryItem.id,
                        is_restockable_on_void: inventoryItem.isRestockableOnVoid,
                        is_restockable_on_refund: inventoryItem.isRestockableOnRefund,
                        low_threshold: inventoryItem.lowThreshold,
                    }
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

export const inventoryItemDeletedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_ITEM_DELETED),
        switchMap((action: Action) => {
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Delete,
                    RecordTypeEnum.InventoryItem,
                    action.payload.id,
                    getVersionById(deps.getState(), action.payload.id),
                    {}
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )
// Inventory Item Location

export const inventoryItemLocationCreatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_ITEM_LOCATION_CREATED),
        switchMap((action: Action) => {
            const inventoryItemLocation: InventoryItemLocation = action.payload.inventoryItemLocation
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Insert,
                    RecordTypeEnum.InventoryItemLocation,
                    inventoryItemLocation.id,
                    getVersionById(deps.getState(), inventoryItemLocation.id),
                    {
                        id: inventoryItemLocation.id,
                        inventory_item_id: inventoryItemLocation.inventoryItemId,
                        location_id: inventoryItemLocation.locationId,
                        origin_timestamp: getUtcTimestamp(),
                    }
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

export const inventoryItemLocationDeletedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_ITEM_LOCATION_DELETED),
        switchMap((action: Action) => {
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Delete,
                    RecordTypeEnum.InventoryItemLocation,
                    action.payload.inventoryItemLocationId,
                    getVersionById(deps.getState(), action.payload.inventoryItemLocationId),
                    {}
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

// todo: remove with MIB location-picker images refactor
export const accountImageDeletedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_IMAGE_DELETED),
        switchMap((action: Action) => {
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Delete,
                    RecordTypeEnum.AccountImage,
                    action.payload.id,
                    getVersionById(deps.getState(), action.payload.id),
                    {}
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

// Inventory Item Transaction

export const inventoryItemTransactionCreatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_ITEM_TRANSACTION_CREATED),
        switchMap((action: Action) => {
            const inventoryItemTransaction: InventoryItemTransaction = action.payload.inventoryItemTransaction
            const arrayOfOperations = [
                createOperation(
                    deps,
                    OperationType.Insert,
                    RecordTypeEnum.InventoryItemTransaction,
                    inventoryItemTransaction.id,
                    getVersionById(deps.getState(), inventoryItemTransaction.id),
                    {
                        id: inventoryItemTransaction.id,
                        inventory_item_location_id: inventoryItemTransaction.inventoryItemLocationId,
                        amount: inventoryItemTransaction.amount,
                        created_user_account_id: inventoryItemTransaction.createdUserAccountId,
                        type: inventoryItemTransaction.type,
                        origin_timestamp: getUtcTimestamp(),
                    }
                ),
            ]
            return of(sendOperationsRequested(arrayOfOperations))
        })
    )

export const sendOperationsEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SEND_OPERATIONS_REQUESTED),
        buffer(actions$.pipe(ofType(SEND_OPERATIONS_REQUESTED), debounceTime(300))),
        switchMap((batch: Record<string, any>[]) =>
            deps.api
                .sendOperations(
                    getHttpHeaders(deps.getState()),
                    createArrayOfOperationsToSend(
                        flatMap(batch, action => action.payload.operations).map(o =>
                            addAuditToOperation(o, getAuthUserId(deps.getState()))
                        ),
                        deps
                    )
                )
                .pipe(
                    map((response: SendOperationsResponse) => {
                        if (response.status === "Success") {
                            return sendOperationsSuccess(flatMap(batch, action => action.payload.operations))
                        } else {
                            return sendOperationsFailure()
                        }
                    }),
                    catchError(error => from([sendOperationsFailure(error)]))
                )
        )
    )

export const successEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SEND_OPERATIONS_SUCCESS),
        delay(1000),
        map(() => sendOperationsClearState())
    )

export default [
    accountImageDeletedEpics,
    inventoryItemTransactionCreatedEpic,
    inventoryItemLocationDeletedEpic,
    inventoryItemCreatedEpic,
    inventoryItemUpdatedEpic,
    inventoryItemDeletedEpic,
    inventoryItemLocationCreatedEpic,
    settingCreatedEpic,
    settingUpdatedEpic,
    accessLevelDeletedEpic,
    accessLevelCreatedEpic,
    accessLevelUpdatedEpic,
    permissionCreatedEpic,
    permissionDeletedEpic,
    createTimeRecordEpic,
    updateTimeRecordEpic,
    deleteTimeRecordEpic,
    sendOperationsEpic,
    successEpic,
]
